// =====================================================================
// src/pages/intl-acca/DNAProfilePage.tsx
// =====================================================================
// National team DNA profile (PRD §2.2). Tournament-specific —
// distinct from club Manager/Referee DNA.
//
// Routes mounted at: /intl-acca/:tournamentId/dna/:teamCode
//
// Every quantitative field on IntlAccaTeamDNA is nullable. The page
// MUST render "Awaiting data" cells when the engine has not produced
// a value (the same engine-validation default the Debrief follows).

import type { IntlAccaTournament } from "../../db/intl-acca-types";
import type {
  IntlAccaTeamDNA,
  Nation,
} from "../../db/intl-acca-types-extended";
import { TournamentStateBanner } from "../../components/intl-acca/TournamentStateBanner";
import { HonestEmptyState } from "../../components/intl-acca/HonestEmptyState";
import { NationFlag } from "../../components/intl-acca/NationFlag";

export interface DNAProfilePageProps {
  loading: boolean;
  error: string | null;
  tournament: IntlAccaTournament | null;
  nation: Nation | null;
  dna: IntlAccaTeamDNA | null;
  /** "Back to roster" — host wires to /intl-acca/:tid/hub?tab=dna. */
  onBack?: () => void;
  /** "See today's slip" — host wires to today's slip if one published. */
  onSeeSlip?: () => void;
}

export function DNAProfilePage(props: DNAProfilePageProps): JSX.Element {
  const { loading, error, tournament, nation, dna, onBack, onSeeSlip } = props;

  if (loading) {
    return (
      <div className="ia-root ia-page-pad" aria-busy="true">
        <div className="ia-eyebrow">Loading profile…</div>
      </div>
    );
  }
  if (error || !nation) {
    return (
      <div className="ia-root ia-page-pad">
        <HonestEmptyState
          variant="custom"
          title="Profile not found."
          body={error ?? "No row in intl_acca_team_dna matched the requested team."}
        />
      </div>
    );
  }

  // dna may legitimately be null pre-tournament — render the empty shell.
  return (
    <div className="ia-root">
      {tournament ? <TournamentStateBanner tournament={tournament} /> : null}

      {/* Breadcrumb */}
      <div
        style={{
          padding: "14px var(--sz-space-7)",
          borderBottom: "1px solid var(--border-faint)",
        }}
      >
        <button
          type="button"
          onClick={onBack}
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            fontFamily: "var(--sz-font-mono)",
            fontSize: "var(--sz-mono-sm)",
            color: "var(--text-tertiary)",
            letterSpacing: "0.18em",
            padding: 0,
          }}
        >
          ← All profiles
        </button>
        <span
          className="ia-mono"
          style={{
            fontSize: "var(--sz-mono-sm)",
            color: "var(--text-quaternary)",
            letterSpacing: "0.18em",
            margin: "0 10px",
          }}
        >
          /
        </span>
        <span
          className="ia-mono"
          style={{
            fontSize: "var(--sz-mono-sm)",
            color: "var(--accent)",
            letterSpacing: "0.18em",
          }}
        >
          {nation.short} · DNA
        </span>
      </div>

      {/* Hero */}
      <header
        className="ia-page-header"
        style={{ alignItems: "center", borderBottom: "1px solid var(--border-faint)" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 28 }}>
          <NationFlag nation={nation} size={120} />
          <div>
            <div className="ia-eyebrow">
              {nation.is_host
                ? "Tournament host · participating"
                : "Participating nation"}
            </div>
            <h1
              style={{
                fontFamily: "var(--sz-font-head)",
                fontSize: "var(--sz-head-xl)",
                fontWeight: 500,
                lineHeight: 1,
                marginTop: 14,
                letterSpacing: "-0.015em",
                color: "var(--text)",
              }}
            >
              {nation.name}
            </h1>
            {dna?.nickname ? (
              <div
                style={{
                  fontFamily: "var(--sz-font-display)",
                  fontSize: 22,
                  color: "var(--text-secondary)",
                  letterSpacing: "0.04em",
                  marginTop: 8,
                }}
              >
                "{dna.nickname}"
              </div>
            ) : null}
          </div>
        </div>

        {/* Quick strip */}
        <div className="ia-dna-strip">
          <StripCell
            label="FRESHNESS"
            value={dna?.squad_freshness.score ?? null}
            sub={dna?.squad_freshness.label ?? "—"}
            tone={
              dna?.squad_freshness.score == null ? null :
              dna.squad_freshness.score > 70 ? "pos" :
              dna.squad_freshness.score > 55 ? "warn" : "neg"
            }
          />
          <StripCell
            label="FORM"
            valueText={dna?.form_last5 ?? null}
            sub="last 5"
          />
          <StripCell
            label="XG / 90"
            value={dna?.recent_xg ?? null}
            format="decimal"
            sub="recent attacks"
          />
          <StripCell
            label="XI CONF"
            value={dna?.expected_xi_confidence ?? null}
            format="pct"
            sub="lineup certainty"
          />
        </div>
      </header>

      {/* Panels */}
      <div className="ia-page-pad">
        {!dna ? (
          <HonestEmptyState
            variant="custom"
            title="DNA not yet built for this tournament."
            body="The engine builds and updates DNA per tournament window. Profiles populate during PRE_TOURNAMENT once qualifier data feeds the model."
          />
        ) : (
          <>
            <div className="ia-dna-grid">
              <Panel title="Manager DNA" eyebrow="Tournament behavior under pressure">
                {dna.manager ? (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 18,
                    }}
                  >
                    <StatRow label="NAME">
                      <span
                        style={{
                          fontFamily: "var(--sz-font-head)",
                          fontSize: "var(--sz-head-sm)",
                          color: "var(--text)",
                        }}
                      >
                        {dna.manager.name}
                      </span>
                    </StatRow>
                    <StatRow label="TENURE">
                      <span className="ia-mono" style={{ color: "var(--text)" }}>
                        {dna.manager.tenure_months}m
                      </span>
                    </StatRow>
                    <StatRow label="TOURNAMENT RECORD">
                      <span className="ia-mono" style={{ fontSize: 13, color: "var(--text)" }}>
                        {dna.manager.tournament_record}
                      </span>
                    </StatRow>
                    <StatRow label="PRESSURE INDEX">
                      <span
                        style={{
                          fontFamily: "var(--sz-font-display)",
                          fontSize: 22,
                          color:
                            dna.manager.pressure_index === "HIGH" ? "var(--neg)" :
                            dna.manager.pressure_index === "CALM" ? "var(--pos)" : "var(--accent)",
                        }}
                      >
                        {dna.manager.pressure_index}
                      </span>
                    </StatRow>
                  </div>
                ) : <EmptyCell>No manager DNA yet</EmptyCell>}
              </Panel>

              <Panel title="Venue DNA" eyebrow="Tournament-specific stadium record">
                {dna.venue_dna.history ? (
                  <>
                    <div
                      style={{
                        fontFamily: "var(--sz-font-head)",
                        fontSize: "var(--sz-head-sm)",
                        color: "var(--text)",
                        lineHeight: 1.2,
                      }}
                    >
                      {dna.venue_dna.history}
                    </div>
                    {dna.venue_dna.detail ? (
                      <div
                        style={{
                          marginTop: 12,
                          fontSize: "var(--sz-body-sm)",
                          color: "var(--text-secondary)",
                          lineHeight: 1.55,
                        }}
                      >
                        {dna.venue_dna.detail}
                      </div>
                    ) : null}
                  </>
                ) : <EmptyCell>No venue record this tournament</EmptyCell>}
              </Panel>

              <Panel
                title="Squad Freshness"
                eyebrow={
                  dna.squad_freshness.score == null
                    ? "Awaiting data"
                    : `Score · ${dna.squad_freshness.score}/100`
                }
              >
                {dna.squad_freshness.score == null ? (
                  <EmptyCell>Freshness model populates pre-tournament</EmptyCell>
                ) : (
                  <>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 18 }}>
                      <div
                        style={{
                          fontFamily: "var(--sz-font-display)",
                          fontSize: 56,
                          lineHeight: 0.9,
                          color:
                            dna.squad_freshness.score > 70 ? "var(--pos)" :
                            dna.squad_freshness.score > 55 ? "var(--accent)" : "var(--neg)",
                        }}
                      >
                        {dna.squad_freshness.score}
                      </div>
                      <div
                        className="ia-mono"
                        style={{
                          fontSize: 11,
                          letterSpacing: "0.22em",
                          color:
                            dna.squad_freshness.score > 70 ? "var(--pos)" :
                            dna.squad_freshness.score > 55 ? "var(--accent)" : "var(--neg)",
                        }}
                      >
                        {dna.squad_freshness.label ?? "—"}
                      </div>
                    </div>
                    {dna.squad_freshness.detail ? (
                      <p
                        style={{
                          marginTop: 12,
                          fontSize: "var(--sz-body-sm)",
                          color: "var(--text-secondary)",
                          lineHeight: 1.55,
                        }}
                      >
                        {dna.squad_freshness.detail}
                      </p>
                    ) : null}
                    <div className="ia-bar-mini" style={{ marginTop: 14 }}>
                      <div
                        className="ia-bar-mini-fill"
                        style={{ width: `${dna.squad_freshness.score}%` }}
                      />
                    </div>
                  </>
                )}
              </Panel>

              <Panel
                title="Rivalry Index"
                eyebrow={
                  dna.rivalry_index
                    ? `Next opponent · vs ${dna.rivalry_index.vs}`
                    : "No upcoming opponent"
                }
              >
                {!dna.rivalry_index || dna.rivalry_index.score == null ? (
                  <EmptyCell>Rivalry index populates once draw + opponent are known</EmptyCell>
                ) : (
                  <>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 18 }}>
                      <div
                        style={{
                          fontFamily: "var(--sz-font-display)",
                          fontSize: 56,
                          lineHeight: 0.9,
                          color: "var(--accent)",
                        }}
                      >
                        {dna.rivalry_index.score}
                      </div>
                      <div
                        className="ia-mono"
                        style={{
                          fontSize: 11,
                          letterSpacing: "0.22em",
                          color: "var(--accent)",
                        }}
                      >
                        /10 · {dna.rivalry_index.label ?? "—"}
                      </div>
                    </div>
                    <p
                      style={{
                        marginTop: 12,
                        fontSize: "var(--sz-body-sm)",
                        color: "var(--text-secondary)",
                        lineHeight: 1.55,
                      }}
                    >
                      Tournament-specific weight. Top-decile (8+) = winner-takes-all psychological pressure.
                    </p>
                    <div className="ia-bar-mini" style={{ marginTop: 14 }}>
                      <div
                        className="ia-bar-mini-fill"
                        style={{ width: `${dna.rivalry_index.score * 10}%` }}
                      />
                    </div>
                  </>
                )}
              </Panel>

              <Panel title="Recent Form" eyebrow="Last 5 competitive · WDL">
                {!dna.form_last5 ? (
                  <EmptyCell>Form vectors require ≥5 recent fixtures</EmptyCell>
                ) : (
                  <div style={{ display: "flex", gap: 10 }}>
                    {dna.form_last5.split("").map((r, i) => (
                      <div key={i} className={`ia-form-cell ia-form-cell-${r}`}>
                        {r}
                      </div>
                    ))}
                  </div>
                )}
              </Panel>

              <Panel title="Set Piece Intelligence" eyebrow="Tournament dead-ball tendencies">
                {dna.set_piece ? (
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
                    <SetPieceCell label="CORNERS / GAME" value={dna.set_piece.corners_per_game} format="decimal" />
                    <SetPieceCell label="CONV. RATE"     value={dna.set_piece.conversion_rate_pct} format="pct-int" />
                    <SetPieceCell label="SET-PIECE xGA/90" value={dna.set_piece.set_piece_xga_per_90} format="decimal" />
                  </div>
                ) : <EmptyCell>Set-piece intel populates after group stage</EmptyCell>}
              </Panel>
            </div>

            {/* CTA back to slip */}
            {onSeeSlip ? (
              <div
                style={{
                  marginTop: 32,
                  padding: 20,
                  background: "var(--bg-elev)",
                  border: "1px solid var(--sz-amber-dim)",
                  borderRadius: "var(--sz-radius-lg)",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 24,
                }}
              >
                <div>
                  <div className="ia-eyebrow">This profile feeds the slip</div>
                  <p
                    className="ia-sub"
                    style={{
                      marginTop: 8,
                      fontSize: "var(--sz-body-md)",
                      maxWidth: 560,
                    }}
                  >
                    {nation.name}'s DNA contributes to today's Focus Acca —
                    squad freshness, manager DNA, and venue record.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onSeeSlip}
                  style={{
                    background: "var(--accent)",
                    color: "var(--sz-text-inverse)",
                    border: "none",
                    borderRadius: "var(--sz-radius-md)",
                    padding: "14px 22px",
                    fontFamily: "var(--sz-font-mono)",
                    fontSize: 11,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  See today's slip →
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

// ─── tiny helpers ────────────────────────────────────────────────
function StripCell({
  label, value, valueText, sub, tone, format,
}: {
  label: string;
  value?: number | null;
  valueText?: string | null;
  sub?: string;
  tone?: "pos" | "neg" | "warn" | null;
  format?: "decimal" | "pct";
}): JSX.Element {
  const color =
    tone === "pos" ? "var(--pos)" :
    tone === "neg" ? "var(--neg)" :
    tone === "warn" ? "var(--warn)" :
    "var(--text)";

  const rendered =
    valueText !== undefined ? valueText :
    value == null ? null :
    format === "decimal" ? value.toFixed(2) :
    format === "pct" ? `${Math.round(value)}%` :
    String(value);

  return (
    <div className="ia-dna-strip-cell">
      <div className="ia-dna-strip-label">{label}</div>
      {rendered === null ? (
        <div className="ia-metric-value-empty" style={{ marginTop: 8 }}>—</div>
      ) : (
        <div className="ia-dna-strip-value" style={{ color }}>{rendered}</div>
      )}
      {sub ? <div className="ia-dna-strip-sub">{sub}</div> : null}
    </div>
  );
}

function Panel({
  title, eyebrow, children,
}: {
  title: string;
  eyebrow: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="ia-dna-panel">
      <div className="ia-eyebrow">{eyebrow}</div>
      <h3
        style={{
          fontFamily: "var(--sz-font-head)",
          fontSize: "var(--sz-head-sm)",
          fontWeight: 500,
          margin: "10px 0 18px",
          color: "var(--text)",
        }}
      >
        {title}
      </h3>
      {children}
    </div>
  );
}

function StatRow({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <div className="ia-metric-label">{label}</div>
      <div style={{ marginTop: 6 }}>{children}</div>
    </div>
  );
}

function EmptyCell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div
      className="ia-mono"
      style={{
        fontSize: "var(--sz-mono-sm)",
        color: "var(--text-tertiary)",
        letterSpacing: "0.18em",
        textTransform: "uppercase",
      }}
    >
      {children}
    </div>
  );
}

function SetPieceCell({
  label, value, format,
}: {
  label: string;
  value: number | null;
  format: "decimal" | "pct-int";
}): JSX.Element {
  const rendered =
    value == null ? "—" :
    format === "decimal" ? value.toFixed(2) :
    `${Math.round(value)}%`;
  return (
    <div>
      <div className="ia-metric-label">{label}</div>
      <div
        style={{
          fontFamily: "var(--sz-font-display)",
          fontSize: 26,
          color: "var(--text)",
          lineHeight: 1,
          marginTop: 6,
        }}
      >
        {rendered}
      </div>
    </div>
  );
}

// =====================================================================
// DATA BINDING CONTRACT
// =====================================================================
//
// const { data: nation }    = await sb.from('nations').select('*').eq('code', teamCode).single();
// const { data: tournament }= await sb.from('intl_acca_tournaments').select('*').eq('id', tid).single();
// const { data: dna }       = await sb.from('intl_acca_team_dna')   // future migration
//                                   .select('*').eq('team', teamCode).eq('tournament_id', tid).maybeSingle();
//
// If `dna` is null, this page still renders with the empty shell —
// that is the engine-validation default until DNA builds.
